import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  PenLine,
  Repeat2,
  ImageIcon,
  FolderClosed,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  type LucideIcon,
} from "lucide-react";
export type StudioSection =
  | "rewrite"
  | "repurpose"
  | "images"
  | "workspace"
  | "ai-coach";

interface StudioSidebarProps {
  section: StudioSection;
  onSelectSection: (section: StudioSection) => void;
  collapsed: boolean;
  onCollapsedChange: (next: boolean) => void;
}

interface NavItem {
  id: StudioSection;
  path: string;
  label: string;
  icon: LucideIcon;
}

// AI Coach intentionally NOT a top-level nav item — surfaces contextually
// inside Rewrite/Repurpose via the "Get coaching help" button. /coach route
// remains accessible via direct URL and via that contextual button.
const NAV_ITEMS: NavItem[] = [
  { id: "rewrite", path: "/rewrite", label: "Rewrite", icon: PenLine },
  { id: "repurpose", path: "/repurpose", label: "Repurpose", icon: Repeat2 },
  { id: "images", path: "/images", label: "Images", icon: ImageIcon },
  { id: "workspace", path: "/workspace", label: "Workspace", icon: FolderClosed },
];

const COLLAPSED_W = 56;
const EXPANDED_W = 240;
const StudioSidebar = ({
  section,
  onSelectSection,
  collapsed,
  onCollapsedChange,
}: StudioSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (item: NavItem) => {
    if (location.pathname !== item.path) {
      navigate(item.path);
    }
    onSelectSection(item.id);
  };

  return (
    <aside
      aria-label="Studio sections"
      className="hidden lg:flex shrink-0 flex-col border-r"
      style={{
        width: collapsed ? COLLAPSED_W : EXPANDED_W,
        background: "#FFFFFF",
        borderRightColor: "rgba(15,23,42,0.08)",
        transition: "width 180ms ease",
      }}
    >
      <div
        className="flex items-center px-2"
        style={{ height: 56, justifyContent: collapsed ? "center" : "flex-end" }}
      >
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100"
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2">
        <ul className="flex flex-col gap-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = section === item.id;
            const iconColor = active ? "#6366F1" : "#64748B";
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => handleClick(item)}
                  aria-current={active ? "page" : undefined}
                  title={collapsed ? item.label : undefined}
                  className="flex w-full items-center rounded-lg transition-colors"
                  style={{
                    minHeight: 44,
                    padding: collapsed ? 0 : "0 10px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: 10,
                    background: active ? "#EEF2FF" : "transparent",
                    color: active ? "#4F46E5" : "#334155",
                    fontWeight: active ? 600 : 500,
                    fontSize: 13,
                  }}
                >
                  <Icon
                    size={18}
                    strokeWidth={active ? 2.2 : 1.8}
                    style={{ color: iconColor, flexShrink: 0 }}
                    aria-hidden
                  />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div
        className="border-t px-2 py-2"
        style={{ borderTopColor: "rgba(15,23,42,0.08)" }}
      >
        <Link
          to="/settings"
          className="flex w-full items-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100"
          style={{
            minHeight: 44,
            padding: collapsed ? 0 : "0 10px",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 10,
            fontSize: 13,
            fontWeight: 500,
            textDecoration: "none",
          }}
          title={collapsed ? "Settings" : undefined}
        >
          <Settings size={18} strokeWidth={1.8} aria-hidden />
          {!collapsed && <span className="truncate">Settings</span>}
        </Link>
      </div>
    </aside>
  );
};

const SIDEBAR_STORAGE_KEY = "vml_sidebar_collapsed";

export function useSidebarCollapsedState(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      /* quota exceeded — silent */
    }
  }, [collapsed]);
  return [collapsed, setCollapsed];
}

export default StudioSidebar;
